import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { faCheckCircle, faExclamationTriangle, faTimes, faPlusCircle, faUserFriends, faPhone, faUsers } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Swal from 'sweetalert2';
import { useSuggestedData } from '../../../../../context/SuggestedDataContext';
import { Modal } from 'react-bootstrap';
import { DataSourceBadge, EditButton } from '../../../../Common';
import { Button, PhoneField } from '../../../../ui';
import { SuggestedDataSection } from '../SuggestedDataLoadState';
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';

const relationships = ['Conyuge', 'Familiar', 'Amigo', 'Compañero de trabajo', 'Vecino', 'Otro'];

const Referrals = forwardRef(({ 
  formData, 
  handleChange, 
  activeSection, 
  sectionName, 
  sectionValidity, 
  setSectionValidity, 
  handleSectionClick,
  isLoadingSuggestions,
  suggestionLoadFailed = false,
  onRetrySuggestions,
  displayMode = 'accordion'
}, ref) => {
  const isContinuousMode = displayMode === 'continuous';
  // Obtener cantidad mínima de referidos desde variable de entorno
  const minReferrals = parseInt(process.env.REACT_APP_MIN_REFERRALS || '1');
  const allowPossibleButInvalid = process.env.REACT_APP_PHONE_ALLOW_POSSIBLE_WARNING === 'true';
  const { suggestedData, selectedData, setSelectedData, setSuggestedData } = useSuggestedData();
  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [referralPhoneMeta, setReferralPhoneMeta] = useState({});
  const [showAllSuggestions, setShowAllSuggestions] = useState(false);
  const [showNewReferralModal, setShowNewReferralModal] = useState(false);
  const [currentReferralIndex, setCurrentReferralIndex] = useState(null);
  const [newReferrals, setNewReferrals] = useState([]);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isEditingReferral, setIsEditingReferral] = useState(false);
  const [editingReferralData, setEditingReferralData] = useState(null);

  const suggestions = suggestedData.referral || [];

  // Filtrar duplicados, sugerencias reemplazadas y ordenar por fecha (más reciente primero)
  const uniqueSuggestions = suggestions
    .filter((sugg, idx, arr) => arr.findIndex(s => s.id === sugg.id) === idx)
    .filter(sugg => !sugg.replacedBy) // Ocultar sugerencias que han sido reemplazadas
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // Obtener las sugerencias a mostrar
  const displayedSuggestions = showAllSuggestions 
    ? uniqueSuggestions 
    : uniqueSuggestions.filter((suggestion, index) => 
        index < 3 || // Mostrar los primeros 3
        suggestion.id.toString().startsWith('new_') || // Mostrar las nuevas
        suggestion.id.toString().startsWith('edited_') || // Mostrar las editadas
        selectedData.referral?.includes(suggestion.id) // Mostrar las seleccionadas
      );

  // Calcular cuántos referidos están ocultos
  const hiddenSuggestionsCount = uniqueSuggestions.filter(suggestion => 
    !displayedSuggestions.includes(suggestion)
  ).length;

  const isPhonePayloadAccepted = (payload) => {
    if (!payload || !payload.e164) return false;
    if (payload.isValid) return true;
    if (allowPossibleButInvalid && payload.isPossible) return true;
    return false;
  };

  const isReferralPhoneValueValid = (phoneValue) => {
    if (!phoneValue) return false;
    try {
      const parsed = parsePhoneNumberFromString(phoneValue);
      if (!parsed) return false;
      if (parsed.isValid()) return true;
      return allowPossibleButInvalid && parsed.isPossible();
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    if (!formData.referral) {
      handleChange({
        target: {
          name: 'referral',
          value: []
        }
      });
    }
  }, []);

  useEffect(() => {
    if (activeSection === sectionName && (selectedData.referral?.length > 0 || touchedFields['referral.0.firstName'])) {
      validateSection();
    }
  }, [selectedData.referral, activeSection, sectionName]);

  const handleUseExisting = (suggestion) => {
    setSelectedData(prev => {
      const currentReferrals = prev.referral || [];
      const isSelected = currentReferrals.includes(suggestion.id);
      
      // Si ya está seleccionado, lo quitamos
      if (isSelected) {
        return {
          ...prev,
          referral: currentReferrals.filter(id => id !== suggestion.id)
        };
      }
      
      // Si no está seleccionado, lo agregamos
      return {
        ...prev,
        referral: [...currentReferrals, suggestion.id]
      };
    });

    // Validar la sección después de cambiar la selección
    validateSection();
  };

  const handleUseNewForm = () => {
    setFormSubmitted(false);
    setIsEditingReferral(false);
    setEditingReferralData(null);
    
    // Inicializar el array de referidos si no existe
    if (!formData.referral) {
      handleChange({
        target: {
          name: 'referral',
          value: []
        }
      });
    }
    
    // Agregar un nuevo referido vacío
    const currentReferrals = Array.isArray(formData.referral) ? formData.referral : [];
    const newIndex = currentReferrals.length;
    
    handleChange({
      target: {
        name: 'referral',
        value: [...currentReferrals, {
          firstName: '',
          lastName: '',
          phone: '',
          relationship: ''
        }]
      }
    });
    
    setCurrentReferralIndex(newIndex);
    setShowNewReferralModal(true);
    setErrors({});
    setTouchedFields({});
  };

  const handleEditReferral = (suggestion) => {
    setIsEditingReferral(true);
    setEditingReferralData(suggestion);
    setShowNewReferralModal(true);
    setFormSubmitted(false);
    
    // Inicializar el array de referidos si no existe
    if (!formData.referral) {
      handleChange({
        target: {
          name: 'referral',
          value: []
        }
      });
    }
    
    // Crear un referido temporal con los datos existentes para editar
    const currentReferrals = Array.isArray(formData.referral) ? formData.referral : [];
    const newIndex = currentReferrals.length;
    
    // Usar los nombres de campo correctos según el tipo de sugerencia
    const firstName = suggestion.id.toString().startsWith('new_') || suggestion.id.toString().startsWith('edited_') 
      ? suggestion.firstName 
      : suggestion.first_name;
    const lastName = suggestion.id.toString().startsWith('new_') || suggestion.id.toString().startsWith('edited_')
      ? suggestion.lastName 
      : suggestion.last_name;
    
    handleChange({
      target: {
        name: 'referral',
        value: [...currentReferrals, {
          firstName: firstName || '',
          lastName: lastName || '',
          phone: suggestion.phone || '',
          relationship: suggestion.relationship || ''
        }]
      }
    });
    
    setCurrentReferralIndex(newIndex);
    setErrors({});
    setTouchedFields({});
  };

  const handleCloseModal = () => {
    setFormSubmitted(false);
    setShowNewReferralModal(false);
    setCurrentReferralIndex(null);
    setIsEditingReferral(false);
    setEditingReferralData(null);
    
    // Eliminar el referido vacío que se creó al abrir el modal
    if (formData.referral && formData.referral.length > 0) {
      handleChange({
        target: {
          name: 'referral',
          value: formData.referral.filter(r => r.firstName || r.lastName || r.phone || r.relationship)
        }
      });
    }
    setErrors({});
    setTouchedFields({});
  };

  const handleSaveNewReferral = () => {
    setFormSubmitted(true);
    const currentReferral = formData.referral[currentReferralIndex];
    
    // Validar campos requeridos
    const requiredFields = ['firstName', 'lastName', 'phone', 'relationship'];
    let hasEmptyFields = false;
    requiredFields.forEach(field => {
      if (!currentReferral[field]) {
        setTouchedFields(prev => ({ ...prev, [`referral.${currentReferralIndex}.${field}`]: true }));
        hasEmptyFields = true;
      }
    });
    if (hasEmptyFields) {
      return;
    }

    const isPhoneValid = validateField('phone', currentReferral.phone, currentReferralIndex);
    if (!isPhoneValid) {
      setTouchedFields(prev => ({ ...prev, [`referral.${currentReferralIndex}.phone`]: true }));
      return;
    }

    // Check for duplicates solo si no estamos editando
    if (!isEditingReferral) {
      const isDuplicate = checkForDuplicates('phone', currentReferral.phone, currentReferralIndex) ||
                         checkForDuplicates('firstName', currentReferral.firstName, currentReferralIndex) ||
                         checkForDuplicates('lastName', currentReferral.lastName, currentReferralIndex);
      if (isDuplicate) {
        return;
      }
    }

    // Crear el objeto del referido (nuevo o editado)
    const referralData = {
      id: isEditingReferral ? `edited_${editingReferralData.id}_${Date.now()}` : `new_${Date.now()}`,
      firstName: currentReferral.firstName,
      lastName: currentReferral.lastName,
      phone: currentReferral.phone,
      relationship: currentReferral.relationship,
      created_at: new Date().toISOString(),
      // Marcar si es una edición para el badge
      isEdited: isEditingReferral,
      originalId: isEditingReferral ? editingReferralData.id : null
    };

    // Si estamos editando, marcar el referido original como reemplazado
    let updatedSuggestions;
    if (isEditingReferral) {
      updatedSuggestions = suggestions.map(suggestion => 
        suggestion.id === editingReferralData.id 
          ? { ...suggestion, replacedBy: referralData.id }
          : suggestion
      );
      // Agregar el nuevo referido editado
      updatedSuggestions = [referralData, ...updatedSuggestions];
    } else {
      // Para nuevos referidos, simplemente agregar al inicio
      updatedSuggestions = [referralData, ...suggestions];
    }
    
    // Seleccionar el referido por defecto
    setSelectedData(prev => ({
      ...prev,
      referral: [...(prev.referral || []), referralData.id]
    }));

    // Actualizar el contexto con las nuevas sugerencias
    setSuggestedData(prev => ({
      ...prev,
      referral: updatedSuggestions
    }));

    // Cerrar el modal y limpiar el formulario
    handleCloseModal();
    
    // Mostrar mensaje de éxito
    Swal.fire({
      icon: 'success',
      title: isEditingReferral ? 'Referido editado' : 'Referido agregado',
      text: isEditingReferral ? 'El referido ha sido editado correctamente.' : 'El nuevo referido ha sido agregado correctamente.',
      timer: 2000,
      showConfirmButton: false
    });
  };

  const handleRemoveReferral = (referralId) => {
    setSelectedData(prev => ({
      ...prev,
      referral: prev.referral?.filter(id => id !== referralId) || []
    }));
    setNewReferrals(prev => prev.filter(ref => ref.id !== referralId));
  };

  const validateField = (name, value, index) => {
    let error = '';

    const field = name.split('.').pop();

    if (['phone', 'firstName', 'lastName'].includes(field)) {
      const isDuplicate = checkForDuplicates(name, value, index);
      if (isDuplicate) {
        error = `El ${name === 'phone' ? 'número de teléfono' : 'referido'} ya existe.`;
      }
    }
  
    switch (field) {
      case 'phone':
        if (!value) {
          error = 'El número de teléfono es requerido';
        } else {
          const phoneMeta = referralPhoneMeta[index];
          if (phoneMeta && !isPhonePayloadAccepted(phoneMeta)) {
            error = 'El número no es válido para el país seleccionado';
          } else if (!phoneMeta && !isReferralPhoneValueValid(value)) {
            error = 'El número no es válido para el país seleccionado';
          }
        }
        break;
      case 'firstName':
      case 'lastName':
        if (!value) {
          error = `${field === 'firstName' ? 'El nombre' : 'El apellido'} es requerido`;
        }
        break;
      case 'relationship':
        if (!value) {
          error = 'El vínculo es requerido';
        }
        break;
      default:
        break;
    }

    if (error) {
      setErrors(prev => ({
        ...prev,
        [`referral.${index}.${field}`]: error
      }));
    }

    return !error;
  };

  const checkForDuplicates = (field, value, index) => {
    const normalizedValue = String(value).trim().toLowerCase();
    const currentReferral = formData.referral[index];
  
    return suggestions.some(suggestion => {
      if (suggestion.id === currentReferral?.id) return false;

      const existingFirstName = String(suggestion.firstName || suggestion.first_name).trim().toLowerCase();
      const existingLastName = String(suggestion.lastName || suggestion.last_name).trim().toLowerCase();
      const existingPhone = String(suggestion.phone).trim().toLowerCase();

      const currentFirstName = String(currentReferral.firstName).trim().toLowerCase();
      const currentLastName = String(currentReferral.lastName).trim().toLowerCase();
      const currentPhone = String(currentReferral.phone).trim().toLowerCase();

      // Check for exact phone match
      if (field === 'phone' && existingPhone === normalizedValue) {
        setErrors(prev => ({
          ...prev,
          [`referral.${index}.phone`]: 'Este número de teléfono ya existe'
        }));
        return true;
      }

      // Check for exact name match
      if ((field === 'firstName' || field === 'lastName') && 
          existingFirstName === currentFirstName && 
          existingLastName === currentLastName) {
        setErrors(prev => ({
          ...prev,
          [`referral.${index}.${field}`]: 'Este referido ya existe'
        }));
        return true;
      }

      // Check for combined match (same name and phone)
      if (existingFirstName === currentFirstName && 
          existingLastName === currentLastName && 
          existingPhone === currentPhone) {
        setErrors(prev => ({
          ...prev,
          [`referral.${index}.${field}`]: 'Este referido ya existe'
        }));
        return true;
      }

      return false;
    });
  };  

  const validateSection = () => {
    const selectedReferrals = selectedData.referral || [];
    
    if (selectedReferrals.length < minReferrals) {
      setSectionValidity(prev => ({ ...prev, [sectionName]: false }));
      return false;
    }

    const selectedReferralsData = suggestions.filter((suggestion) =>
      selectedReferrals.includes(suggestion.id)
    );

    const hasInvalidCustomReferralPhone = selectedReferralsData.some((suggestion) => {
      const suggestionId = String(suggestion.id ?? '');
      const isCustomReferral = suggestionId.startsWith('new_') || suggestionId.startsWith('edited_');
      if (!isCustomReferral) return false;
      return !isReferralPhoneValueValid(String(suggestion.phone || '').trim());
    });

    if (hasInvalidCustomReferralPhone) {
      setSectionValidity(prev => ({ ...prev, [sectionName]: false }));
      return false;
    }

    setSectionValidity(prev => ({ ...prev, [sectionName]: true }));
    return true;
  };

  const clearFieldError = (fieldPath) => {
    setErrors(prev => {
      if (!prev[fieldPath]) return prev;
      const next = { ...prev };
      delete next[fieldPath];
      return next;
    });
  };

  const handlePhoneChange = (e, index) => {
    const numericValue = e.target.value.replace(/\D/g, '');
    if (numericValue.length <= 13) {
      handleDynamicChange(e, index, 'phone', numericValue);
    }
  };

  const handleReferralPhonePayload = (payload, index, markTouched = false) => {
    const idx = index ?? currentReferralIndex ?? 0;
    clearFieldError(`referral.${idx}.phone`);
    setReferralPhoneMeta(prev => ({ ...prev, [idx]: payload || null }));
    if (markTouched) {
      setTouchedFields(prev => ({ ...prev, [`referral.${idx}.phone`]: true }));
    }
    handleChange(
      { target: { name: 'phone', value: payload.e164 || '' } },
      { isNested: true, arrayIndex: idx, nestedKey: 'referral' }
    );
  };

  const handleDynamicChange = (e, index, field, valueOverride = null) => {
    const fieldName = `referral.${index}.${field}`;
    clearFieldError(fieldName);
    setTouchedFields(prev => ({
      ...prev,
      [fieldName]: true
    }));

    handleChange(
      { target: { name: field, value: valueOverride ?? e.target.value } },
      { isNested: true, arrayIndex: index, nestedKey: 'referral' }
    );
  };

  const showExplanationDialog = () => {
    Swal.fire({
      title: '¿Para qué necesitamos referidos?',
      html: `
        <div class="text-start">
          <p class="mb-3">
            Los referidos son contactos de confianza que pueden dar fe de tu identidad. 
            No son co-deudores ni tendrán ninguna responsabilidad sobre la compra. 
            Solo serán contactados para verificar tu identidad en caso de ser necesario.
          </p>
        </div>
      `,
      confirmButtonText: 'Entendido',
      customClass: {
        container: 'my-swal'
      }
    });
  };

  const handleFieldBlur = (field, value, index) => {
    const fieldName = `referral.${index}.${field}`;
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
    const isValid = validateField(field, value, index);
    if (isValid) {
      clearFieldError(fieldName);
    }
  };

  useImperativeHandle(ref, () => ({
    validateSection,
  }));

  // Renderizado condicional basado en el modo de visualización
  if (isContinuousMode) {
    return (
      <div className="continuous-section mb-2">
        <div className="section-header mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="d-flex align-items-center mb-0 fw-light rg-sale-section__title">
              <FontAwesomeIcon icon={faUserFriends} className="me-2" />
              Referidos
              {sectionValidity[sectionName] === true && (
                <FontAwesomeIcon icon={faCheckCircle} className="text-success ms-2" />
              )}
              {sectionValidity[sectionName] === false && (
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger ms-2" />
              )}
            </h5>
            <Button
              variant="outlinePrimary"
              size="sm"
              onClick={handleUseNewForm}
            >
              <i className="bi bi-plus-circle me-1" aria-hidden />
              Nuevo Referido
            </Button>
          </div>
        </div>
        <div className="section-content">
          <SuggestedDataSection
            loading={isLoadingSuggestions}
            failed={suggestionLoadFailed}
            loadingLabel="Cargando referidos sugeridos..."
            onRetry={onRetrySuggestions}
          >
            <div className="suggested-data">
              <div className="d-flex align-items-center mb-3">
                <button
                  type="button"
                  className="btn btn-link text-primary p-0 text-decoration-none"
                  onClick={showExplanationDialog}
                >
                  <i className="bi bi-info-circle me-1"></i>
                  ¿Para qué necesitamos referidos?
                </button>
              </div>
              <div className="row g-1">
                {displayedSuggestions.map(suggestion => (
                  <div key={suggestion.id} className="col-12">
                    <div className="card suggested-referral-card mb-1 position-relative">
                      <div className="card-body">
                        <div className="form-check d-flex align-items-center">
                          <input
                            className="form-check-input mt-0"
                            type="checkbox"
                            name="referralSelection"
                            id={`referral-${suggestion.id}`}
                            onChange={() => handleUseExisting(suggestion)}
                            checked={selectedData.referral?.includes(suggestion.id)}
                            style={{ marginTop: 0, marginBottom: 0 }}
                          />
                          <label className="form-check-label w-100 ms-2" htmlFor={`referral-${suggestion.id}`} style={{ cursor: 'pointer' }}>
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <p className="mb-2 fw-medium lh-1">
                                  {suggestion.id.toString().startsWith('new_') || suggestion.id.toString().startsWith('edited_')
                                    ? `${suggestion.firstName} ${suggestion.lastName}`
                                    : `${suggestion.first_name} ${suggestion.last_name}`
                                  }
                                </p>
                                <small className="text-muted d-block lh-1" style={{ fontSize: '0.75rem' }}>
                                  <FontAwesomeIcon icon={faPhone} className="me-2" style={{ fontSize: '0.7rem' }} />
                                  {suggestion.phone}
                                  <span className="mx-2">•</span>
                                  <FontAwesomeIcon icon={faUsers} className="me-1" style={{ fontSize: '0.7rem' }} />
                                  {suggestion.relationship}
                                </small>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                {suggestion.id.toString().startsWith('new_') && (
                                  <span className="badge bg-primary">Nuevo</span>
                                )}
                              </div>
                            </div>
                          </label>
                        </div>
                        <DataSourceBadge dataItem={suggestion} options={{ position: 'top-right' }} />
                        {/* Botón de edición - mostrar para referidos originales y editados, pero no para nuevos */}
                        {(!suggestion.id.toString().startsWith('new_') || suggestion.id.toString().startsWith('edited_')) && false && (
                          <EditButton 
                            onEdit={() => handleEditReferral(suggestion)} 
                            options={{ position: 'bottom-right', variant: 'muted' }} 
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <div className="col-12 mt-3 d-flex justify-content-between align-items-center">
                  <div>
                    {!showAllSuggestions && hiddenSuggestionsCount > 0 && (
                      <button 
                        className="btn btn-link text-decoration-none"
                        onClick={() => setShowAllSuggestions(true)}
                      >
                        Ver {hiddenSuggestionsCount} más
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </SuggestedDataSection>
        </div>

        {/* Modal para nuevo referido */}
        <Modal show={showNewReferralModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{isEditingReferral ? 'Editar Referido' : 'Nuevo Referido'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Nombre<sup>*</sup></label>
                      <input
                        type="text"
                        className={`form-control ${(errors[`referral.${currentReferralIndex || 0}.firstName`] && (formSubmitted || touchedFields[`referral.${currentReferralIndex || 0}.firstName`])) ? 'is-invalid' : ''}`}
                        value={formData.referral?.[currentReferralIndex || 0]?.firstName || ''}
                        onChange={(e) => handleDynamicChange(e, currentReferralIndex || 0, 'firstName')}
                        onBlur={(e) => handleFieldBlur('firstName', e.target.value, currentReferralIndex || 0)}
                        required
                        autoComplete="off"
                      />
                      {(errors[`referral.${currentReferralIndex || 0}.firstName`] && (formSubmitted || touchedFields[`referral.${currentReferralIndex || 0}.firstName`])) && (
                        <div className="invalid-feedback">{errors[`referral.${currentReferralIndex || 0}.firstName`]}</div>
                      )}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Apellido<sup>*</sup></label>
                      <input
                        type="text"
                        className={`form-control ${(errors[`referral.${currentReferralIndex || 0}.lastName`] && (formSubmitted || touchedFields[`referral.${currentReferralIndex || 0}.lastName`])) ? 'is-invalid' : ''}`}
                        value={formData.referral?.[currentReferralIndex || 0]?.lastName || ''}
                        onChange={(e) => handleDynamicChange(e, currentReferralIndex || 0, 'lastName')}
                        onBlur={(e) => handleFieldBlur('lastName', e.target.value, currentReferralIndex || 0)}
                        required
                        autoComplete="off"
                      />
                      {(errors[`referral.${currentReferralIndex || 0}.lastName`] && (formSubmitted || touchedFields[`referral.${currentReferralIndex || 0}.lastName`])) && (
                        <div className="invalid-feedback">{errors[`referral.${currentReferralIndex || 0}.lastName`]}</div>
                      )}
                    </div>
                    <div className="col-md-6 mb-3">
                      <PhoneField
                        id={`referral-phone-${currentReferralIndex ?? 0}`}
                        name={`referral.${currentReferralIndex ?? 0}.phone`}
                        label="Teléfono"
                        placeholder="Ej: 11 2345 6789"
                        defaultCountry="AR"
                        required
                        showE164Helper={false}
                        valueE164={formData.referral?.[currentReferralIndex || 0]?.phone || ''}
                        error={(errors[`referral.${currentReferralIndex || 0}.phone`] && (formSubmitted || touchedFields[`referral.${currentReferralIndex || 0}.phone`])) ? errors[`referral.${currentReferralIndex || 0}.phone`] : ''}
                        onChange={(payload) => handleReferralPhonePayload(payload, currentReferralIndex || 0, false)}
                        onBlur={(payload) => {
                          handleReferralPhonePayload(payload, currentReferralIndex || 0, true);
                          handleFieldBlur('phone', payload?.e164 ?? '', currentReferralIndex || 0);
                        }}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Relación<sup>*</sup></label>
                      <select
                        className={`form-select ${(errors[`referral.${currentReferralIndex || 0}.relationship`] && (formSubmitted || touchedFields[`referral.${currentReferralIndex || 0}.relationship`])) ? 'is-invalid' : ''}`}
                        value={formData.referral?.[currentReferralIndex || 0]?.relationship || ''}
                        onChange={(e) => handleDynamicChange(e, currentReferralIndex || 0, 'relationship')}
                        onBlur={(e) => handleFieldBlur('relationship', e.target.value, currentReferralIndex || 0)}
                        required
                      >
                        <option value="">Selecciona una relación</option>
                        {relationships.map((relationship) => (
                          <option key={relationship} value={relationship}>
                            {relationship}
                          </option>
                        ))}
                      </select>
                      {(errors[`referral.${currentReferralIndex || 0}.relationship`] && (formSubmitted || touchedFields[`referral.${currentReferralIndex || 0}.relationship`])) && (
                        <div className="invalid-feedback">{errors[`referral.${currentReferralIndex || 0}.relationship`]}</div>
                      )}
                    </div>
                  </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="d-flex justify-content-between w-100">
              <Button type="button" variant="secondary" onClick={handleCloseModal}>
                Cancelar
              </Button>
              <Button type="button" variant="primary" onClick={handleSaveNewReferral}>
                {isEditingReferral ? 'Guardar Cambios' : 'Guardar Referido'}
              </Button>
            </div> 
          </Modal.Footer>
        </Modal>
      </div>
    );
  }

  // Modo accordion (original)
  return (
    <div className="accordion-item mb-1">
      <h2 className="accordion-header" id={`heading${sectionName}`}>
        <button
          className={`accordion-button ${activeSection !== sectionName ? 'collapsed' : ''}`}
          type="button"
          onClick={() => handleSectionClick(sectionName)}
        >
          <FontAwesomeIcon icon={faUserFriends} className="me-2" />
          Referidos{' '}
          {sectionValidity[sectionName] === true && (
            <FontAwesomeIcon icon={faCheckCircle} className="text-success ms-2" />
          )}
          {sectionValidity[sectionName] === false && (
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger ms-2" />
          )}
        </button>
      </h2>
      <div
        className={`accordion-collapse collapse ${activeSection === sectionName ? 'show' : ''}`}
        aria-labelledby={`heading${sectionName}`}
      >
        <div className="accordion-body">
          <SuggestedDataSection
            loading={isLoadingSuggestions}
            failed={suggestionLoadFailed}
            loadingLabel="Cargando referidos sugeridos..."
            onRetry={onRetrySuggestions}
          >
            <div className="suggested-data">
              <div className="d-flex align-items-center mb-3">
                <button
                  type="button"
                  className="btn btn-link text-primary p-0 text-decoration-none"
                  onClick={showExplanationDialog}
                >
                  <i className="bi bi-info-circle me-1"></i>
                  ¿Para qué necesitamos referidos?
                </button>
              </div>
              <div className="d-flex justify-content-end mb-3">
                <Button
                  variant="outlinePrimary"
                  size="sm"
                  onClick={handleUseNewForm}
                >
                  <i className="bi bi-plus-circle me-1" aria-hidden />
                  Nuevo Referido
                </Button>
              </div>
              <div className="row g-1">
                {displayedSuggestions.map(suggestion => (
                  <div key={suggestion.id} className="col-12">
                    <div className="card suggested-referral-card mb-1 position-relative">
                      <div className="card-body">
                        <div className="form-check d-flex align-items-center">
                          <input
                            className="form-check-input mt-0"
                            type="checkbox"
                            name="referralSelection"
                            id={`referral-${suggestion.id}`}
                            onChange={() => handleUseExisting(suggestion)}
                            checked={selectedData.referral?.includes(suggestion.id)}
                            style={{ marginTop: 0, marginBottom: 0 }}
                          />
                          <label className="form-check-label w-100 ms-2" htmlFor={`referral-${suggestion.id}`} style={{ cursor: 'pointer' }}>
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <p className="mb-2 fw-medium lh-1">
                                  {suggestion.id.toString().startsWith('new_') || suggestion.id.toString().startsWith('edited_')
                                    ? `${suggestion.firstName} ${suggestion.lastName}`
                                    : `${suggestion.first_name} ${suggestion.last_name}`
                                  }
                                </p>
                                <small className="text-muted d-block lh-1" style={{ fontSize: '0.75rem' }}>
                                  <FontAwesomeIcon icon={faPhone} className="me-2" style={{ fontSize: '0.7rem' }} />
                                  {suggestion.phone}
                                  <span className="mx-2">•</span>
                                  <FontAwesomeIcon icon={faUsers} className="me-1" style={{ fontSize: '0.7rem' }} />
                                  {suggestion.relationship}
                                </small>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                {suggestion.id.toString().startsWith('new_') && (
                                  <span className="badge bg-primary">Nuevo</span>
                                )}
                              </div>
                            </div>
                          </label>
                        </div>
                        {/* Badge de origen de datos */}
                        <DataSourceBadge dataItem={suggestion} options={{ position: 'top-right' }} />
                        {/* Botón de edición - mostrar para referidos originales y editados, pero no para nuevos */}
                        {(!suggestion.id.toString().startsWith('new_') || suggestion.id.toString().startsWith('edited_')) && false && (
                          <EditButton 
                            onEdit={() => handleEditReferral(suggestion)} 
                            options={{ position: 'bottom-right', variant: 'muted' }} 
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <div className="col-12 mt-3 d-flex justify-content-between align-items-center">
                  <div>
                    {!showAllSuggestions && hiddenSuggestionsCount > 0 && (
                      <button 
                        className="btn btn-link text-decoration-none"
                        onClick={() => setShowAllSuggestions(true)}
                      >
                        Ver {hiddenSuggestionsCount} más
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </SuggestedDataSection>
        </div>
      </div>

      {/* Modal para nuevo referido */}
      <Modal show={showNewReferralModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isEditingReferral ? 'Editar Referido' : 'Nuevo Referido'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Nombre<sup>*</sup></label>
                    <input
                      type="text"
                      className={`form-control ${(errors[`referral.${currentReferralIndex || 0}.firstName`] && (formSubmitted || touchedFields[`referral.${currentReferralIndex || 0}.firstName`])) ? 'is-invalid' : ''}`}
                      value={formData.referral?.[currentReferralIndex || 0]?.firstName || ''}
                      onChange={(e) => handleDynamicChange(e, currentReferralIndex || 0, 'firstName')}
                      onBlur={(e) => handleFieldBlur('firstName', e.target.value, currentReferralIndex || 0)}
                      required
                      autoComplete="off"
                    />
                    {(errors[`referral.${currentReferralIndex || 0}.firstName`] && (formSubmitted || touchedFields[`referral.${currentReferralIndex || 0}.firstName`])) && (
                      <div className="invalid-feedback">{errors[`referral.${currentReferralIndex || 0}.firstName`]}</div>
                    )}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Apellido<sup>*</sup></label>
                    <input
                      type="text"
                      className={`form-control ${(errors[`referral.${currentReferralIndex || 0}.lastName`] && (formSubmitted || touchedFields[`referral.${currentReferralIndex || 0}.lastName`])) ? 'is-invalid' : ''}`}
                      value={formData.referral?.[currentReferralIndex || 0]?.lastName || ''}
                      onChange={(e) => handleDynamicChange(e, currentReferralIndex || 0, 'lastName')}
                      onBlur={(e) => handleFieldBlur('lastName', e.target.value, currentReferralIndex || 0)}
                      required
                      autoComplete="off"
                    />
                    {(errors[`referral.${currentReferralIndex || 0}.lastName`] && (formSubmitted || touchedFields[`referral.${currentReferralIndex || 0}.lastName`])) && (
                      <div className="invalid-feedback">{errors[`referral.${currentReferralIndex || 0}.lastName`]}</div>
                    )}
                  </div>
                  <div className="col-md-6 mb-3">
                    <PhoneField
                      id={`referral-phone-acc-${currentReferralIndex ?? 0}`}
                      name={`referral.${currentReferralIndex ?? 0}.phone`}
                      label="Teléfono"
                      placeholder="Ej: 11 2345 6789"
                      defaultCountry="AR"
                      required
                      showE164Helper={false}
                      valueE164={formData.referral?.[currentReferralIndex || 0]?.phone || ''}
                      error={(errors[`referral.${currentReferralIndex || 0}.phone`] && (formSubmitted || touchedFields[`referral.${currentReferralIndex || 0}.phone`])) ? errors[`referral.${currentReferralIndex || 0}.phone`] : ''}
                      onChange={(payload) => handleReferralPhonePayload(payload, currentReferralIndex || 0, false)}
                      onBlur={(payload) => {
                        handleReferralPhonePayload(payload, currentReferralIndex || 0, true);
                        handleFieldBlur('phone', payload?.e164 ?? '', currentReferralIndex || 0);
                      }}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Relación<sup>*</sup></label>
                    <select
                      className={`form-select ${(errors[`referral.${currentReferralIndex || 0}.relationship`] && (formSubmitted || touchedFields[`referral.${currentReferralIndex || 0}.relationship`])) ? 'is-invalid' : ''}`}
                      value={formData.referral?.[currentReferralIndex || 0]?.relationship || ''}
                      onChange={(e) => handleDynamicChange(e, currentReferralIndex || 0, 'relationship')}
                      onBlur={(e) => handleFieldBlur('relationship', e.target.value, currentReferralIndex || 0)}
                      required
                    >
                      <option value="">Selecciona una relación</option>
                      {relationships.map((relationship) => (
                        <option key={relationship} value={relationship}>
                          {relationship}
                        </option>
                      ))}
                    </select>
                    {(errors[`referral.${currentReferralIndex || 0}.relationship`] && (formSubmitted || touchedFields[`referral.${currentReferralIndex || 0}.relationship`])) && (
                      <div className="invalid-feedback">{errors[`referral.${currentReferralIndex || 0}.relationship`]}</div>
                    )}
                  </div>
                </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <Button type="button" variant="secondary" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button type="button" variant="primary" onClick={handleSaveNewReferral}>
              {isEditingReferral ? 'Guardar Cambios' : 'Guardar Referido'}
            </Button>
          </div> 
        </Modal.Footer>
      </Modal>
    </div>
  );
});

export default Referrals;